import { buildImprovementOpportunitiesInputEvidence } from '@process-advisor/process-miner/consultant/consultantPromptDataNormalizer';

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/,/g, '').replace(/%/g, '').trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
};

const clamp = (num, min, max) => Math.min(max, Math.max(min, num));

const toTitleCase = (value) => String(value || '').replace(/(^|\s)\S/g, (s) => s.toUpperCase());


const MAX_SAMPLE_ITEMS_IN_AI_EVIDENCE = 100;
const MAX_SAMPLE_FIELDS_PER_ITEM = 24;
const MAX_SAMPLE_STRING_LENGTH = 220;

const SAMPLE_FIELD_DENYLIST = new Set([
  'variant id',
  'variant_id',
  'duration',
  'duration_seconds',
  'duration hours',
  'duration_hours',
  'avg_duration_hours',
  'std_deviation_hours',
  'sla max business percentage',
  'sla business percentage',
]);

const isUsefulSampleField = (key) => {
  const normalized = String(key || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized.startsWith('_')) return false;
  if (SAMPLE_FIELD_DENYLIST.has(normalized)) return false;
  return true;
};

const trimSampleValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.slice(0, 6).map(trimSampleValue);
  if (typeof value === 'object') return undefined;

  const text = String(value);
  return text.length > MAX_SAMPLE_STRING_LENGTH
    ? `${text.slice(0, MAX_SAMPLE_STRING_LENGTH)}…`
    : text;
};

const compactSampleItem = (row = {}, index = 0) => {
  if (!row || typeof row !== 'object') return { sample_index: index + 1, value: trimSampleValue(row) };

  const preferredKeys = [
    'Task #',
    'Number',
    'number',
    'priority',
    'Priority',
    'assignment_group',
    'Assignment group',
    'assigned_to',
    'Assigned to',
    'category',
    'Category',
    'subcategory',
    'Subcategory',
    'state',
    'State',
    'status',
    'Status',
    'impact',
    'Impact',
    'urgency',
    'Urgency',
    'business_service',
    'Business service',
    'service_offering',
    'Service offering',
    'contact_type',
    'Contact type',
    'opened_at',
    'Opened',
    'resolved_at',
    'Resolved',
    'closed_at',
    'Closed',
  ];

  const orderedKeys = [
    ...preferredKeys.filter((key) => Object.prototype.hasOwnProperty.call(row, key)),
    ...Object.keys(row).filter((key) => !preferredKeys.includes(key)),
  ];

  const compacted = { sample_index: index + 1 };
  for (const key of orderedKeys) {
    if (Object.keys(compacted).length > MAX_SAMPLE_FIELDS_PER_ITEM) break;
    if (!isUsefulSampleField(key)) continue;
    const value = trimSampleValue(row[key]);
    if (value === undefined || value === null || value === '') continue;
    compacted[key] = value;
  }
  return compacted;
};

const buildSampleEvidence = (sampleItems = []) => {
  const rows = Array.isArray(sampleItems) ? sampleItems : [];
  const sampleRows = rows.slice(0, MAX_SAMPLE_ITEMS_IN_AI_EVIDENCE).map(compactSampleItem);

  return {
    sample_size: rows.length,
    sample_items_included: sampleRows.length,
    sample_selection: 'Random/scoped sample items supplied by Process Advisor after report-scope and privacy rules have been applied.',
    usage_guidance: 'Use these sample items only when item-level examples, data-quality checks, or validation are necessary. Do not let sample rows override model-level metrics, transitions, variants, SLA signals, or attribute_usage.',
    rows: sampleRows,
  };
};


const inferCanonicalHealth = (payload = {}) => {
  const processSummary = payload.process_summary || {};
  const timeIntelligence = payload.time_intelligence || {};
  const workloadSignals = payload.workload_signals || {};
  const slaSignals = payload.sla_signals || {};
  const dataCoverage = payload.data_coverage || {};
  const topDelayTransitions = payload.transition_signals?.top_delay_transitions || [];
  const topVariants = payload.variant_signals?.top_variants || [];

  const totalItems = toNumber(processSummary.total_items) || 0;
  const variantsTotal = toNumber(processSummary.variants_total) || 0;
  const reworkPct = toNumber(processSummary.rework_rate_percent) || 0;
  const avgTransitions = toNumber(processSummary.avg_transitions) || 0;
  const flowEfficiencyPct = toNumber(timeIntelligence.flow_efficiency_percent);
  const adminHours = toNumber(timeIntelligence.avg_admin_time_hours);
  const queueHours = toNumber(timeIntelligence.avg_queue_time_hours);
  const cycleHours = toNumber(timeIntelligence.avg_cycle_time_hours);
  const sampleItemsAvailable = !!dataCoverage.sample_items;
  const timeAvailable = !!dataCoverage.time_intelligence;

  let score = 6.2;
  const drivers = [];
  let penalty = 0;
  let bonus = 0;

  const addPenalty = (amount, reason) => {
    penalty += amount;
    drivers.push({ type: 'penalty', amount, reason });
  };
  const addBonus = (amount, reason) => {
    bonus += amount;
    drivers.push({ type: 'bonus', amount, reason });
  };

  if (variantsTotal >= 150) addPenalty(1.0, `Very high process fragmentation (${variantsTotal} variants)`);
  else if (variantsTotal >= 100) addPenalty(0.8, `High process fragmentation (${variantsTotal} variants)`);
  else if (variantsTotal >= 60) addPenalty(0.55, `Elevated process fragmentation (${variantsTotal} variants)`);
  else if (variantsTotal >= 30) addPenalty(0.3, `Moderate process fragmentation (${variantsTotal} variants)`);

  if (reworkPct >= 35) addPenalty(1.35, `Severe rework (${reworkPct.toFixed(0)}%)`);
  else if (reworkPct >= 25) addPenalty(1.0, `High rework (${reworkPct.toFixed(0)}%)`);
  else if (reworkPct >= 15) addPenalty(0.6, `Elevated rework (${reworkPct.toFixed(0)}%)`);
  else if (reworkPct >= 8) addPenalty(0.3, `Notable rework (${reworkPct.toFixed(0)}%)`);

  if (avgTransitions >= 9) addPenalty(0.5, `High average handoffs (${avgTransitions.toFixed(1)} transitions)`);
  else if (avgTransitions >= 7) addPenalty(0.3, `Elevated average handoffs (${avgTransitions.toFixed(1)} transitions)`);
  else if (avgTransitions >= 5.5) addPenalty(0.15, `Moderate handoff complexity (${avgTransitions.toFixed(1)} transitions)`);

  if (timeAvailable) {
    const operationalDelayHours = Math.max(adminHours || 0, queueHours || 0);
    if (operationalDelayHours >= 168) addPenalty(0.8, `Very high non-touch delay (${operationalDelayHours.toFixed(0)}h)`);
    else if (operationalDelayHours >= 72) addPenalty(0.55, `High non-touch delay (${operationalDelayHours.toFixed(0)}h)`);
    else if (operationalDelayHours >= 24) addPenalty(0.3, `Meaningful non-touch delay (${operationalDelayHours.toFixed(0)}h)`);

    if (flowEfficiencyPct !== null) {
      if (flowEfficiencyPct < 10) addPenalty(0.7, `Very low flow efficiency (${flowEfficiencyPct.toFixed(1)}%)`);
      else if (flowEfficiencyPct < 20) addPenalty(0.45, `Low flow efficiency (${flowEfficiencyPct.toFixed(1)}%)`);
      else if (flowEfficiencyPct < 35) addPenalty(0.2, `Moderate flow efficiency (${flowEfficiencyPct.toFixed(1)}%)`);
      else if (flowEfficiencyPct >= 55) addBonus(0.35, `Healthy flow efficiency (${flowEfficiencyPct.toFixed(1)}%)`);
    }

    if (cycleHours !== null && cycleHours <= 24 && reworkPct <= 10) {
      addBonus(0.2, 'Cycle time is comparatively controlled');
    }
  }

  const attributeUsage = Array.isArray(workloadSignals.attribute_usage)
    ? workloadSignals.attribute_usage
    : (Array.isArray(workloadSignals.field_usage_highlights) ? workloadSignals.field_usage_highlights : []);

  const populationRateOf = (field) => toNumber(field?.population_rate ?? field?.populated_pct ?? field?.populated_percent) ?? 0;
  const distinctValuesOf = (field) => toNumber(field?.distinct_values) ?? 0;
  const classificationStrengthOf = (field) => String(field?.classification_strength || '').toLowerCase();

  const highlyUsedFields = attributeUsage.filter((field) => populationRateOf(field) >= 80);
  const sparseFields = attributeUsage.filter((field) => {
    const population = populationRateOf(field);
    return population > 0 && population < 70;
  });
  const classificationFields = attributeUsage.filter((field) => {
    const strength = classificationStrengthOf(field);
    return strength === 'strong' || strength === 'moderate' || distinctValuesOf(field) > 1;
  });

  if (highlyUsedFields.length >= 3) addBonus(0.3, 'Core attributes are well populated');
  else if (highlyUsedFields.length >= 1) addBonus(0.15, 'Some core attributes are well populated');

  if (classificationFields.length >= 2) addBonus(0.25, 'Usable attribute structure is present');
  else if (classificationFields.length === 1) addBonus(0.1, 'Some attribute structure is present');

  if (sparseFields.length >= 5) addPenalty(0.35, 'Several sparse attributes weaken reporting and automation readiness');
  else if (sparseFields.length >= 3) addPenalty(0.2, 'Some sparse attributes weaken reporting and automation readiness');

  const solvedSignals = attributeUsage
    .flatMap((item) => (Array.isArray(item?.top_values) ? item.top_values.map((v) => v?.label || v?.value || '') : []))
    .join(' ')
    .toLowerCase();
  if (solvedSignals.includes('solved') || solvedSignals.includes('closed') || solvedSignals.includes('resolved')) {
    addBonus(0.25, 'Outcomes appear to be consistently recorded');
  }

  if (slaSignals?.available) {
    const breachedPct = toNumber(slaSignals.breached_items_percent) || 0;
    const avgElapsed = toNumber(slaSignals.avg_business_elapsed_hours) || 0;
    if (breachedPct >= 30) addPenalty(0.7, `High SLA breach exposure (${breachedPct.toFixed(0)}% of SLA-linked sampled items)`);
    else if (breachedPct >= 15) addPenalty(0.4, `Elevated SLA breach exposure (${breachedPct.toFixed(0)}%)`);
    else if (breachedPct > 0) addPenalty(0.15, `Some SLA breach exposure (${breachedPct.toFixed(0)}%)`);
    if (avgElapsed >= 72) addPenalty(0.25, `Long SLA business elapsed time (${avgElapsed.toFixed(0)}h average)`);
  }

  if (sampleItemsAvailable) addBonus(0.15, 'Broad evidence coverage is available');
  if (totalItems >= 200) addBonus(0.1, 'Score is supported by a meaningful sample size');
  if (!timeAvailable) drivers.push({ type: 'neutral', amount: 0, reason: 'Time intelligence unavailable — not penalised' });

  const severeLongTail = variantsTotal >= Math.max(40, Math.round(totalItems * 0.08));
  if (severeLongTail && topVariants.length >= 5) addPenalty(0.2, 'Long-tail path complexity remains material');

  const worstDelay = topDelayTransitions.reduce((mx, row) => Math.max(mx, toNumber(row?.avg_duration_hours) || 0), 0);
  if (timeAvailable && worstDelay >= 120) addPenalty(0.25, `A major delayed transition is visible (${worstDelay.toFixed(0)}h)`);

  penalty = Math.min(penalty, 3.9);
  bonus = Math.min(bonus, 1.6);
  score = clamp(Number((score - penalty + bonus).toFixed(1)), 1.5, 9.2);

  let rating = 'Moderate';
  if (score < 3) rating = 'Poor';
  else if (score < 4.5) rating = 'Weak';
  else if (score < 6) rating = 'Fair';
  else if (score < 7.5) rating = 'Good';
  else rating = 'Strong';

  let evidencePoints = 0;
  if (dataCoverage.process_metrics) evidencePoints += 1;
  if (dataCoverage.transitions) evidencePoints += 1;
  if (dataCoverage.variants) evidencePoints += 1;
  if (dataCoverage.field_usage) evidencePoints += 1;
  if (dataCoverage.activity_model || dataCoverage.status_types) evidencePoints += 1;
  if (sampleItemsAvailable) evidencePoints += 1;
  if (timeAvailable) evidencePoints += 1;
  if (dataCoverage.sla_metrics || slaSignals?.available) evidencePoints += 1;

  let confidence = 'Moderate';
  if (evidencePoints >= 6) confidence = 'High';
  else if (evidencePoints <= 3) confidence = 'Low';

  const topDrivers = drivers
    .filter((item) => item.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 3)
    .map((item) => item.reason);

  const rationale = topDrivers.length > 0
    ? topDrivers.join('; ')
    : 'Score based on the overall balance of process structure, delay, and data quality signals';

  return {
    version: 'shared-consultant-health-v3',
    score,
    rating,
    confidence,
    rationale,
    score_display: Number(score.toFixed(1)),
    score_band: rating,
    top_drivers: topDrivers,
    driver_breakdown: drivers,
  };
};

const debugSharedConsultantPayload = () => {
  // Intentionally silent in production. Keep this hook so callers do not need to change.
};

export const buildSharedConsultantPayload = (args = {}) => {
  const payload = buildImprovementOpportunitiesInputEvidence(args);
  const sampleEvidence = buildSampleEvidence(args.sampleItems || []);
  const finalPayload = {
    ...payload,
    canonical_health: inferCanonicalHealth(payload),
    sample_evidence: sampleEvidence,
  };
  debugSharedConsultantPayload(finalPayload, args);
  return finalPayload;
};
